import { useEffect, useState } from "react";
import { Link } from "wouter";
import { Crown } from "lucide-react";
import { useAuth } from "@/_core/hooks/useAuth";
import { trpc } from "@/lib/trpc";
import { accessSource } from "@/lib/proAccess";

function formatRemaining(msRemaining: number): string {
  if (msRemaining <= 0) return "Expired";

  const totalMinutes = Math.floor(msRemaining / 60000);
  const days = Math.floor(totalMinutes / (60 * 24));
  const hours = Math.floor((totalMinutes % (60 * 24)) / 60);
  const minutes = totalMinutes % 60;

  if (days > 0) return `${days}d ${hours}h left`;
  if (hours > 0) return `${hours}h ${minutes}m left`;
  return `${minutes}m left`;
}

/**
 * Small "Pro" pill shown in the header whenever the signed-in user has active
 * Day Pass / Monthly access, with a live countdown to expiry. Renders nothing
 * for free users or logged-out visitors.
 */
export function ProStatusBadge() {
  const { isAuthenticated } = useAuth();
  const billingStatus = trpc.billing.status.useQuery(undefined, {
    enabled: isAuthenticated,
    refetchInterval: 60_000, // re-check with the server every minute
  });

  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), 30_000); // tick the countdown every 30s
    return () => clearInterval(interval);
  }, []);

  // Where the access comes from decides the label (see lib/proAccess.ts). It used to read only the
  // personal plan date, so a covered organization/team member showed "Day Pass · Expired".
  const source = isAuthenticated ? accessSource(billingStatus.data, now) : null;
  if (!source?.until) return null;

  const remainingLabel = formatRemaining(source.until.getTime() - now);
  const planLabel = source.shortLabel;

  return (
    <Link href="/profile">
      <div className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-primary/10 border border-primary/20 hover:bg-primary/15 transition-colors cursor-pointer">
        <Crown className="h-3.5 w-3.5 text-primary" />
        <span className="text-xs font-medium text-primary">{planLabel} · {remainingLabel}</span>
      </div>
    </Link>
  );
}
